import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import MarketingNav from "@/components/app/MarketingNav";
import OtterIcon from "@/components/app/OtterIcon";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPost(null); setError(false);
    (async () => {
      try {
        const { data } = await api.get(`/blog/posts/${slug}`);
        setPost(data);
        document.title = `${data.title} — ArloSEO`;
        const meta = document.querySelector('meta[name="description"]');
        if (meta && data.excerpt) meta.setAttribute("content", data.excerpt);
      } catch {
        setError(true);
      }
    })();
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <MarketingNav />
        <main className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="blog-post-not-found">
          <div className="w-14 h-14 mx-auto bg-foreground grid place-items-center text-background mb-4">
            <OtterIcon size={36} />
          </div>
          <h1 className="font-semibold tracking-tight text-3xl">Post not found.</h1>
          <p className="mt-2 text-sm text-muted-foreground">Arlo may have eaten it. (Sorry.)</p>
          <Link to="/blog" className="mt-6 inline-flex items-center gap-1.5 underline underline-offset-4">
            <ArrowLeft className="w-4 h-4" /> Back to the blog
          </Link>
        </main>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <MarketingNav />
        <main className="max-w-2xl mx-auto px-6 py-24 text-sm text-muted-foreground" data-testid="blog-post-loading">Loading…</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="blog-post-page">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="blog-post-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to journal
        </Link>

        {/* Meta */}
        <div className="mt-6 flex items-center gap-3 font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
          {post.target_keyword && (
            <span className="border border-border bg-secondary/50 px-1.5 py-0.5">{post.target_keyword}</span>
          )}
          <span>{formatDate(post.published_at)}</span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {post.reading_minutes} min read</span>
        </div>

        {/* Title */}
        <h1 className="font-semibold tracking-tight mt-4 text-4xl sm:text-5xl leading-[1.05]" data-testid="blog-post-title">
          {post.title}
        </h1>

        {/* Byline */}
        <div className="mt-6 pb-6 border-b border-border flex items-center gap-3">
          <div className="w-9 h-9 bg-foreground grid place-items-center text-background">
            <OtterIcon size={22} />
          </div>
          <div>
            <div className="text-sm font-medium">Arlo</div>
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
              drafted by Arlo · edited by the ArloSEO team
            </div>
          </div>
        </div>

        {/* Content */}
        <article
          className="prose prose-zinc max-w-none mt-10 prose-headings:font-semibold prose-headings:tracking-tight prose-h2:text-2xl prose-h2:mt-12 prose-h3:text-xl prose-p:leading-relaxed prose-a:underline-offset-4 prose-li:leading-relaxed"
          data-testid="blog-post-content"
          dangerouslySetInnerHTML={{ __html: post.html }}
        />

        {/* CTA */}
        <div className="mt-16 border border-foreground bg-foreground text-background p-8 relative overflow-hidden" data-testid="blog-post-cta">
          <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
          <div className="relative">
            <span className="font-mono-data text-[10px] uppercase tracking-widest text-background/60">// drafted by Arlo · using ArloSEO</span>
            <h3 className="font-semibold tracking-tight text-2xl sm:text-3xl mt-3 leading-tight">
              Want Arlo to draft your next post?
            </h3>
            <p className="mt-3 text-background/70 max-w-xl">
              ArloSEO is the AI SEO workspace that audits, scores, writes, and publishes — so you can ship more content without the grind.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild className="rounded-none bg-background text-foreground hover:bg-background/90">
                <Link to="/register" data-testid="blog-post-cta-signup">
                  Start free <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-none border-background text-background hover:bg-background hover:text-foreground">
                <Link to="/blog" data-testid="blog-post-cta-more">More posts</Link>
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
